import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup,
  sendEmailVerification
} from "firebase/auth";
import { auth, database } from "../Firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Container, Button, Form, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";

const POINTS_VALIDATION = 250;

export default function Connexion() {
  const navigate                              = useNavigate();
  const [email, setEmail]                     = useState("");
  const [password, setPassword]               = useState("");
  const [error, setError]                     = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [emailNonVerifie, setEmailNonVerifie] = useState(false);
  const [renvoye, setRenvoye]                 = useState(false);
  const [showPassword, setShowPassword]       = useState(false);

  const checkUserStatus = async (user) => {
    try {
      const userSnap = await getDoc(doc(database, 'users', user.uid));
      if (!userSnap.exists()) { navigate("/user"); return; }
      const data = userSnap.data();

      if (data.role === "admin") { navigate("/admin/dashboard"); return; }

      if (!user.emailVerified) { setEmailNonVerifie(true); return; }

      if (!data.emailVerifie) {
        await updateDoc(doc(database, 'users', user.uid), {
          emailVerifie: true,
          ...(data.pointsParrainageEnAttente > 0 && {
            points: (data.points || 0) + POINTS_VALIDATION,
            pointsParrainageEnAttente: 0,
          }),
        });
        if (data.parrain && data.pointsParrainageEnAttente > 0) {
          const parrainSnap = await getDoc(doc(database, 'users', data.parrain));
          if (parrainSnap.exists()) {
            await updateDoc(doc(database, 'users', data.parrain), {
              points: (parrainSnap.data().points || 0) + POINTS_VALIDATION,
              pointsParrainageEnAttente: 0,
            });
          }
        }
      }
      navigate("/");
    } catch (err) {
      console.error("Erreur vérification statut:", err);
      navigate("/");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setEmailNonVerifie(false);
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await checkUserStatus(result.user);
    } catch (err) {
      if (['auth/user-not-found','auth/wrong-password','auth/invalid-credential'].includes(err.code))
        setError("Email ou mot de passe incorrect.");
      else if (err.code === 'auth/too-many-requests')
        setError("Trop de tentatives. Réessayez dans quelques minutes.");
      else
        setError("Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result   = await signInWithPopup(auth, provider);
      await checkUserStatus(result.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setError("Échec de l'authentification Google.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setError(null);
    try {
      const user = auth.currentUser;
      if (user) {
        await sendEmailVerification(user, {
          url: `${window.location.origin}/login`,
          handleCodeInApp: false,
        });
        setRenvoye(true);
        setTimeout(() => setRenvoye(false), 5000);
      }
    } catch (err) {
      if (err.code === 'auth/too-many-requests')
        setError("Trop de demandes. Attendez quelques minutes.");
      else
        setError("Erreur lors du renvoi. Réessayez.");
    }
  };

  const handleCheckVerification = async () => {
    setError(null);
    try {
      await auth.currentUser?.reload();
      const user = auth.currentUser;
      if (user?.emailVerified) {
        setEmailNonVerifie(false);
        await checkUserStatus(user);
      } else {
        setError("Email pas encore vérifié. Vérifiez votre boîte mail.");
      }
    } catch (err) {
      setError("Erreur lors de la vérification. Réessayez.");
    }
  };

  // ── Écran email non vérifié ───────────────────────────────────────────────
  if (emailNonVerifie) {
    return (
      <Container className="py-4 px-3" style={{ maxWidth: '480px' }}>
        <div style={{
          background: '#fff', borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
          padding: '32px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '3rem' }}>📧</div>
          <h4 style={{ fontWeight: 700, marginTop: '12px', fontSize: '1.2rem' }}>
            Vérifiez votre email
          </h4>
          <p className="text-muted mt-2" style={{ fontSize: '0.88rem' }}>
            Cliquez sur le lien envoyé à{' '}
            <strong style={{ wordBreak: 'break-all' }}>{email}</strong>{' '}
            pour activer votre compte.
          </p>

          <Alert variant="warning" style={{ fontSize: '0.82rem', textAlign: 'left' }}>
            ⚠️ Vérifiez également vos <strong>spams</strong>.
          </Alert>

          {renvoye && (
            <Alert variant="success" style={{ fontSize: '0.82rem' }}>
              ✅ Email renvoyé !
            </Alert>
          )}
          {error && (
            <Alert variant="danger" style={{ fontSize: '0.82rem' }}>
              {error}
            </Alert>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
            <Button
              style={{
                background: '#07a993', border: 'none',
                fontWeight: 700, padding: '14px',
                fontSize: '0.95rem', borderRadius: '10px',
              }}
              onClick={handleCheckVerification}>
              ✅ J'ai vérifié mon email
            </Button>

            <Button
              variant="outline-secondary"
              style={{ padding: '12px', borderRadius: '10px', fontSize: '0.9rem' }}
              onClick={handleResendEmail}>
              📨 Renvoyer l'email
            </Button>

            <Button
              variant="link"
              style={{ color: '#dc3545', fontSize: '0.82rem' }}
              onClick={async () => {
                await auth.signOut();
                setEmailNonVerifie(false);
                setError(null);
                setEmail("");
                setPassword("");
              }}>
              Se déconnecter et utiliser un autre compte
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  // ── Formulaire connexion ──────────────────────────────────────────────────
  return (
    <Container className="py-4 px-3" style={{ maxWidth: '480px' }}>
      <div style={{
        background: '#fff', borderRadius: '16px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
        padding: '32px 24px',
      }}>

        {/* Logo / Titre */}
        <div className="text-center mb-4">
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🅿️</div>
          <h2 style={{ fontWeight: 700, fontSize: '1.5rem', marginBottom: '4px' }}>
            Connexion
          </h2>
          <p style={{ fontSize: '0.85rem', color: '#6c757d', margin: 0 }}>
            Bienvenue sur SpotyPark
          </p>
        </div>

        {error && (
          <Alert variant="danger" style={{ fontSize: '0.85rem', borderRadius: '8px' }}>
            {error}
          </Alert>
        )}

        {/* Bouton Google */}
        <Button
          variant="outline-danger"
          className="w-100 mb-3"
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{
            padding: '12px', borderRadius: '10px',
            fontSize: '0.95rem', fontWeight: 600,
          }}>
          <i className="bi bi-google me-2"></i>
          {loading ? 'Connexion...' : 'Continuer avec Google'}
        </Button>

        {/* Séparateur */}
        <div style={{ position: 'relative', textAlign: 'center', margin: '16px 0' }}>
          <hr style={{ margin: 0 }} />
          <span style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff', padding: '0 12px',
            color: '#6c757d', fontSize: '0.82rem',
          }}>
            ou avec votre email
          </span>
        </div>

        {/* Formulaire */}
        <Form onSubmit={handleLogin}>

          {/* Email */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>
              Email
            </Form.Label>
            <Form.Control
              type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              required autoComplete="email"
              inputMode="email"
              placeholder="votre@email.fr"
              style={{ fontSize: '16px', padding: '12px', borderRadius: '8px' }}
            />
          </Form.Group>

          {/* Mot de passe */}
          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>
              Mot de passe
            </Form.Label>
            <div style={{ position: 'relative' }}>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required autoComplete="current-password"
                placeholder="••••••••"
                style={{
                  fontSize: '16px', padding: '12px',
                  paddingRight: '48px', borderRadius: '8px',
                }}
              />
              {/* Bouton afficher/masquer mot de passe */}
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                style={{
                  position: 'absolute', right: '12px', top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none',
                  cursor: 'pointer', color: '#6c757d',
                  fontSize: '0.85rem', padding: '4px',
                  minWidth: '36px', minHeight: '36px',
                }}>
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </Form.Group>

          {/* Bouton submit */}
          <Button
            type="submit"
            className="w-100"
            disabled={loading}
            style={{
              background: 'linear-gradient(135deg, #07a993, #0d6efd)',
              border: 'none', fontWeight: 700,
              padding: '14px', fontSize: '1rem',
              borderRadius: '10px',
            }}>
            {loading ? 'Connexion en cours...' : 'Se connecter'}
          </Button>
        </Form>

        {/* Lien inscription */}
        <div className="text-center mt-4" style={{ fontSize: '0.88rem' }}>
          Pas encore de compte ?{' '}
          <Link to="/inscription" style={{ fontWeight: 700, color: '#07a993' }}>
            S'inscrire gratuitement
          </Link>
        </div>

      </div>
    </Container>
  );
}